import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Users, Search, X } from 'lucide-react';

export default function ContactSelect({ value, onChange, placeholder = 'Cerca contatto...', className = '' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState(value?.name || '');
  const ref = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/crm/contacts', { params: { search: query, limit: 8 } });
        setResults(res.data.contacts || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (contact) => {
    onChange({ id: contact.id, name: contact.full_name, email: contact.email });
    setSelectedName(contact.full_name);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setSelectedName('');
    setQuery('');
  };

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="contact-select">
      {selectedName ? (
        <div className="flex items-center gap-2 h-8 px-3 border border-gray-200 rounded-md bg-[#F8FAFC] text-sm">
          <Users className="w-3 h-3 text-[#2E75B6]" />
          <span className="flex-1 text-[#1A3A5C] font-medium">{selectedName}</span>
          <button onClick={clear} className="text-[#94A3B8] hover:text-red-500"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#94A3B8]" />
          <Input className="h-8 text-xs pl-7" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder} onFocus={() => results.length > 0 && setOpen(true)} />
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map(c => (
            <button key={c.id} onClick={() => select(c)}
              className="w-full text-left px-3 py-2 hover:bg-[#F8FAFC] border-b border-[#F1F5F9] last:border-0"
              data-testid={`contact-option-${c.id}`}>
              <p className="text-sm font-medium text-[#1A3A5C]">{c.full_name}</p>
              <p className="text-xs text-[#64748B]">{c.email}{c.company ? ` — ${c.company}` : ''}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
